import type { Schema } from '@dealdesk/api-schema';

import type { DealAction } from './stages';

/** The explicit command endpoint segment of each transition action (POST /deals/{dealId}/commands/{command}). */
export type DealCommand =
  | 'submit-to-operations'
  | 'request-documents'
  | 'return-to-sales'
  | 'submit-to-bank'
  | 'record-bank-logged'
  | 'record-bank-query'
  | 'respond-to-query'
  | 'record-approval'
  | 'record-rejection'
  | 'start-post-approval'
  | 'record-booking'
  | 'confirm-disbursal'
  | 'cancel';

export const COMMAND_OF: Partial<Record<DealAction, DealCommand>> = {
  submit_to_ops: 'submit-to-operations',
  await_documents: 'request-documents',
  ops_return: 'return-to-sales',
  submit_to_bank: 'submit-to-bank',
  mark_bank_logged: 'record-bank-logged',
  record_bank_query: 'record-bank-query',
  respond_query: 'respond-to-query',
  bank_approve: 'record-approval',
  bank_reject: 'record-rejection',
  start_post_approval: 'start-post-approval',
  mark_booked: 'record-booking',
  disburse: 'confirm-disbursal',
  cancel: 'cancel',
};

type Req = Schema<'TransitionRequest'>;
const pick = <K extends keyof Req>(r: Req, ...keys: K[]) => Object.fromEntries(keys.filter((k) => r[k] !== undefined && r[k] !== '').map((k) => [k, r[k]])) as Partial<Pick<Req, K>>;

/**
 * Builds the command body from the dialog's transition-shaped request. Only the fields each command's contract schema
 * declares are sent.
 */
export function commandBody(req: Req): Record<string, unknown> {
  switch (req.action) {
    case 'submit_to_ops':
    case 'start_post_approval':
    case 'respond_query':
      return pick(req, 'note');
    case 'await_documents':
      return pick(req, 'note');
    case 'ops_return':
      return pick(req, 'note', 'reasonCode');
    case 'submit_to_bank':
      return pick(req, 'bankReference', 'note');
    case 'mark_bank_logged':
      return pick(req, 'encodedOn', 'bankReference', 'note');
    case 'record_bank_query':
      return pick(req, 'note', 'dueOn', 'reasonCode');
    case 'bank_approve':
      return pick(req, 'approvedAmount', 'offer', 'note');
    case 'bank_reject':
      return pick(req, 'reasonCode', 'note');
    case 'mark_booked':
      return pick(req, 'bookedAmount', 'bookedOn', 'note');
    case 'disburse':
      return pick(req, 'disbursedAmount', 'disbursedOn', 'note');
    case 'cancel':
      return pick(req, 'closedReason', 'note');
    default:
      return {};
  }
}
